import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Tag, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { imgSrc } from "@/lib/img";

export default function Checkout() {
  const { items, subtotal } = useCart();
  const { user, API, authHeader } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [ship, setShip] = useState({
    name: user?.name || "",
    address: "",
    city: "",
    state: "",
    postal: "",
    country: "India",
    phone: "",
  });
  const [code, setCode] = useState("");
  const [applying, setApplying] = useState(false);
  const [coupon, setCoupon] = useState(null); // { code, discount, new_total }

  const upd = (k) => (e) => setShip(s => ({ ...s, [k]: e.target.value }));

  const applyCoupon = async () => {
    const v = code.trim();
    if (!v) { toast.error("Enter a code"); return; }
    setApplying(true);
    try {
      const { data } = await axios.post(
        `${API}/coupons/validate`,
        { code: v, subtotal },
        { headers: authHeader() }
      );
      setCoupon(data);
      toast.success(`Coupon applied — saved $${data.discount.toFixed(2)}`);
    } catch (err) {
      setCoupon(null);
      toast.error(err.response?.data?.detail || "Coupon invalid");
    } finally { setApplying(false); }
  };

  const removeCoupon = () => { setCoupon(null); setCode(""); };

  const discount = coupon ? coupon.discount : 0;
  const total = Math.max(0, subtotal - discount);

  const pay = async (e) => {
    e.preventDefault();
    if (items.length === 0) { toast.error("Your cart is empty"); return; }
    setBusy(true);
    try {
      const payload = {
        items: items.map(i => ({
          product_id: i.product_id,
          name: i.name,
          price: i.price,
          image: i.image,
          size: i.size,
          color: i.color,
          quantity: i.quantity,
        })),
        shipping: ship,
        origin_url: window.location.origin,
        coupon_code: coupon ? coupon.code : null,
      };
      const { data } = await axios.post(`${API}/checkout/session`, payload, { headers: authHeader() });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="label-eyebrow">Checkout</div>
      <h1 className="font-display text-5xl md:text-6xl mt-3 mb-12">Complete your order</h1>

      <form onSubmit={pay} className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-8">
          <div>
            <h2 className="font-display text-2xl mb-6">Shipping address</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                ["Full name", "name", true, "md:col-span-2"],
                ["Address", "address", true, "md:col-span-2"],
                ["City", "city", true],
                ["State", "state", true],
                ["Postal code", "postal", true],
                ["Country", "country", true],
                ["Phone", "phone", true, "md:col-span-2"],
              ].map(([label, k, req, cls]) => (
                <div key={k} className={cls || ""}>
                  <label className="label-eyebrow">{label}</label>
                  <input
                    required={req}
                    value={ship[k]}
                    onChange={upd(k)}
                    data-testid={`ship-${k}`}
                    className="mt-2 w-full bg-transparent border-b border-[#E5DFD3] py-3 focus:outline-none focus:border-[#6B1A24]"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[#F3EFEA] p-6 text-xs text-[#5C5454]">
            <strong className="block text-[#2D2626] uppercase tracking-[0.2em] text-[10px] mb-2">Secure Payment</strong>
            You'll be redirected to Stripe to complete payment safely. Use test card <span className="font-mono">4242 4242 4242 4242</span>, any future date, any CVC.
          </div>
        </div>

        <aside className="bg-[#F3EFEA] p-8 h-fit lg:sticky lg:top-24">
          <div className="label-eyebrow">Your order</div>
          <div className="mt-6 divide-y divide-[#D1C7BD]">
            {items.map(i => (
              <div key={i._key} className="py-3 flex gap-3 text-sm">
                <div className="w-14 h-16 bg-[#FAFAF7] shrink-0">{i.image && <img src={imgSrc(i.image)} alt="" className="w-full h-full object-cover" />}</div>
                <div className="flex-1">
                  <div className="font-display text-base">{i.name}</div>
                  <div className="text-xs text-[#5C5454]">{i.size}{i.color ? ` · ${i.color}` : ""} × {i.quantity}</div>
                </div>
                <div className="font-display tabular-nums">${(i.price * i.quantity).toFixed(2)}</div>
              </div>
            ))}
          </div>

          {/* Coupon section */}
          <div className="mt-6 pt-6 border-t border-[#D1C7BD]">
            {!coupon ? (
              <>
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#6B1A24]">
                  <Tag size={14} strokeWidth={1.5} /> Promo Code
                </div>
                <div className="mt-3 flex gap-2">
                  <input
                    data-testid="coupon-input"
                    value={code}
                    onChange={e => setCode(e.target.value.toUpperCase())}
                    placeholder="Enter code"
                    className="flex-1 bg-transparent border-b border-[#2D2626] py-2 text-sm uppercase tracking-wider focus:outline-none focus:border-[#6B1A24]"
                  />
                  <button
                    type="button"
                    onClick={applyCoupon}
                    disabled={applying || !code.trim()}
                    data-testid="coupon-apply"
                    className="text-xs uppercase tracking-[0.2em] text-[#6B1A24] px-3 disabled:opacity-50"
                  >
                    {applying ? "…" : "Apply"}
                  </button>
                </div>
                <div className="mt-2 text-[10px] text-[#5C5454] uppercase tracking-[0.15em]">Try WELCOME10 or BAZAAR20</div>
              </>
            ) : (
              <div data-testid="coupon-applied" className="flex items-center justify-between bg-[#FAFAF7] border border-[#6B1A24] px-3 py-2">
                <div className="flex items-center gap-2 text-sm">
                  <Tag size={14} strokeWidth={1.5} className="text-[#6B1A24]" />
                  <span className="font-mono text-xs">{coupon.code}</span>
                  <span className="text-xs text-[#5C5454]">−${coupon.discount.toFixed(2)}</span>
                </div>
                <button type="button" onClick={removeCoupon} data-testid="coupon-remove" className="text-[#5C5454] hover:text-[#6B1A24]">
                  <X size={14} strokeWidth={1.5} />
                </button>
              </div>
            )}
          </div>

          <div className="mt-6 space-y-2 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span className="tabular-nums">${subtotal.toFixed(2)}</span></div>
            {coupon && (
              <div className="flex justify-between text-[#6B1A24]" data-testid="checkout-discount">
                <span>Discount ({coupon.code})</span>
                <span className="tabular-nums">−${discount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-[#5C5454]"><span>Shipping</span><span>Free</span></div>
          </div>
          <div className="border-t border-[#D1C7BD] my-5"></div>
          <div className="flex justify-between font-display text-2xl"><span>Total</span><span data-testid="checkout-total" className="tabular-nums">${total.toFixed(2)}</span></div>
          <button
            type="submit"
            disabled={busy || items.length === 0}
            data-testid="checkout-pay-btn"
            className="mt-8 w-full bg-[#6B1A24] text-[#FAFAF7] py-4 uppercase text-xs tracking-[0.25em] hover:bg-[#8A2532] transition-colors disabled:opacity-60"
          >
            {busy ? "Redirecting…" : "Pay with Stripe"}
          </button>
        </aside>
      </form>
    </div>
  );
}
